import { z } from "zod"

import { portalRequestItemKinds } from "../../portal-requests.js"
import { selectableDocumentTypeKeys } from "../../registries.js"
import { ApiWorkspacePrimaryContactSchema } from "./organizations.js"
import { portalFirmFileStatuses } from "./portal.js"

/**
 * The public client surface: the `/v1/clients` routes an API key reaches with
 * the `clients:read`, `requests:write`, `files:read`, and `files:write`
 * scopes, and the MCP tools built on them.
 *
 * A client is a workspace. Its id is the workspace id, so the same UUID goes in
 * the `{clientId}` path segment here and in the `x-workspace-id` header of the
 * document routes. These schemas are the one source for the API's OpenAPI
 * output and for the SDK's hand-written types (pinned by a parity test).
 */

/** Whether a workspace holds a client's books or the firm's own. */
export const apiClientKinds = ["client", "own_books"] as const

export const ApiClientSummarySchema = z.object({
  clientId: z.string().uuid(),
  name: z.string(),
  companyName: z.string().nullable(),
  kind: z.enum(apiClientKinds),
  portalLinked: z.boolean(),
  /** Contacts that can still sign in. */
  contactCount: z.number().int().nonnegative(),
  primaryContact: ApiWorkspacePrimaryContactSchema.nullable(),
  clientSince: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
})

export type ApiClientSummary = z.infer<typeof ApiClientSummarySchema>

export const ApiClientListQuerySchema = z.object({
  /** Matches the client's name or company name, case-insensitive. */
  search: z.string().trim().max(120).optional(),
  includeArchived: z.enum(["true", "false"]).optional(),
})

export const ApiClientListResponseSchema = z.object({
  clients: z.array(ApiClientSummarySchema),
})

export type ApiClientListResponse = z.infer<typeof ApiClientListResponseSchema>

export const ApiClientContactSchema = z.object({
  contactId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
  invitedAt: z.string().datetime().nullable(),
  lastSeenAt: z.string().datetime().nullable(),
})

/** One open request, with how much of it the client has sent. */
export const ApiClientRequestSchema = z.object({
  requestId: z.string().uuid(),
  kind: z.enum(["period", "adhoc"]),
  /** "August 2026", or the request's title. */
  label: z.string(),
  templateId: z.string().uuid().nullable(),
  periodType: z.enum(["month", "quarter", "year"]).nullable(),
  periodStart: z.string().date().nullable(),
  dueAt: z.string().datetime().nullable(),
  requiredCount: z.number().int().nonnegative(),
  receivedCount: z.number().int().nonnegative(),
  acceptedCount: z.number().int().nonnegative(),
  overdue: z.boolean(),
  /** When the client said they sent everything. */
  clientDoneAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  nextScheduledSendAt: z.string().datetime().nullable(),
})

export type ApiClientRequest = z.infer<typeof ApiClientRequestSchema>

export const ApiClientDetailResponseSchema = z.object({
  client: ApiClientSummarySchema.extend({
    notes: z.string().nullable(),
    contacts: z.array(ApiClientContactSchema),
    openRequests: z.array(ApiClientRequestSchema),
  }),
})

export type ApiClientDetailResponse = z.infer<
  typeof ApiClientDetailResponseSchema
>

export const ApiClientRequestListResponseSchema = z.object({
  requests: z.array(ApiClientRequestSchema),
})

export type ApiClientRequestListResponse = z.infer<
  typeof ApiClientRequestListResponseSchema
>

export const ApiRequestTemplateSchema = z.object({
  templateId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(["period", "adhoc"]),
  items: z.array(
    z.object({
      name: z.string(),
      kind: z.enum(portalRequestItemKinds),
      required: z.boolean(),
    })
  ),
  /**
   * False when the list holds a document to sign. A signature item needs its
   * PDF chosen in LedgerBox, so the API cannot send that list.
   */
  sendableByApi: z.boolean(),
})

export type ApiRequestTemplate = z.infer<typeof ApiRequestTemplateSchema>

export const ApiRequestTemplateListResponseSchema = z.object({
  templates: z.array(ApiRequestTemplateSchema),
})

export type ApiRequestTemplateListResponse = z.infer<
  typeof ApiRequestTemplateListResponseSchema
>

/**
 * A request made from a saved request list. A period list needs the period;
 * an ad hoc list takes its title from the list unless `title` is given.
 */
export const ApiCreateClientRequestBodySchema = z.object({
  templateId: z.string().uuid(),
  /** The date the client should send everything by, as YYYY-MM-DD. */
  dueDate: z.string().date(),
  periodType: z.enum(["month", "quarter", "year"]).optional(),
  /** The first day of the period, as YYYY-MM-DD. */
  periodStart: z.string().date().optional(),
  title: z.string().trim().min(1).max(120).optional(),
})

export type ApiCreateClientRequestBody = z.infer<
  typeof ApiCreateClientRequestBodySchema
>

export const ApiCreateClientRequestResponseSchema = z.object({
  request: ApiClientRequestSchema,
  /** False when the same request was already open, so nothing was sent. */
  created: z.boolean(),
})

export type ApiCreateClientRequestResponse = z.infer<
  typeof ApiCreateClientRequestResponseSchema
>

export const ApiSendReminderResponseSchema = z.object({
  requestId: z.string().uuid(),
  /** Contacts the reminder went to. Zero when nothing is outstanding. */
  sent: z.number().int().nonnegative(),
})

export type ApiSendReminderResponse = z.infer<
  typeof ApiSendReminderResponseSchema
>

/** Files the API lists, by where extraction stands. */
export const apiClientFileFilters = ["all", "not_extracted", "extracted"] as const

export const ApiClientFileSchema = z.object({
  fileId: z.string().uuid(),
  fileName: z.string(),
  byteSize: z.number().int().nonnegative(),
  contentType: z.string(),
  /** `held` is ready to extract; `processing` and `processed` have a document. */
  status: z.enum(portalFirmFileStatuses),
  receivedAt: z.string().datetime(),
  uploaderEmail: z.string().nullable(),
  requestItemId: z.string().uuid().nullable(),
  /** The document extraction made from this file, once it started. */
  documentId: z.string().uuid().nullable(),
  classifiedType: z.string().nullable(),
  classifiedPeriod: z.string().nullable(),
})

export type ApiClientFile = z.infer<typeof ApiClientFileSchema>

export const ApiClientFileListQuerySchema = z.object({
  status: z.enum(apiClientFileFilters).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
})

export const ApiClientFileListResponseSchema = z.object({
  files: z.array(ApiClientFileSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    total: z.number().int(),
  }),
})

export type ApiClientFileListResponse = z.infer<
  typeof ApiClientFileListResponseSchema
>

export const ApiExtractClientFileBodySchema = z.object({
  /** Omit to let LedgerBox detect the document type. */
  documentTypeKey: z.enum(selectableDocumentTypeKeys).optional(),
})

export const ApiExtractClientFileResponseSchema = z.object({
  fileId: z.string().uuid(),
  documentId: z.string().uuid(),
  /** True when extraction had already started for this file. */
  alreadyStarted: z.boolean(),
})

export type ApiExtractClientFileResponse = z.infer<
  typeof ApiExtractClientFileResponseSchema
>
